/**
 * @file        src/modals/BaggageModal.jsx
 * @description "Baggage allowance & policies" modal with three tabbed panes:
 *              Booking Information, Baggage allowance, Cancellation & change.
 *              Ported from `#baggageModal` (~line 2158) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-22 Claude — Initial port. `bagTab()` mutation replaced with a
 *                         `useState` tab index.
 */
import { useEffect, useState } from 'react';

const TABS = ['Booking Information', 'Baggage allowance', 'Cancellation & change'];

export default function BaggageModal({ open, onClose }) {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setTab(0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-3xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel no-scrollbar">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-panel z-10">
          <h2 className="font-display text-2xl font-extrabold">Baggage Allowance &amp; Policies</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
          </button>
        </div>
        <div className="flex items-center gap-1 px-6 border-b border-line text-sm">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className={`bagtab px-3 py-3 font-semibold cursor-pointer border-b-2 ${tab === i ? 'text-neon border-neon' : 'text-muted hover:text-ink border-transparent'}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="p-6">
          {tab === 0 && (
            <div className="bagpane">
              <h3 className="font-display font-bold mb-3">Booking Information</h3>
              <div className="flex items-start gap-2 text-sm">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon mt-0.5"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
                <div>
                  <p className="font-semibold">Ticket issuing time</p>
                  <p className="text-muted">Once payment is confirmed, tickets will be issued within 12 hours.</p>
                </div>
              </div>
            </div>
          )}
          {tab === 1 && (
            <div className="bagpane">
              <h3 className="font-display font-bold mb-2">Baggage allowance</h3>
              <p className="text-muted text-sm mb-4">The total weight of the personal item and carry-on baggage cannot exceed 7 kg.</p>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-neon shrink-0"><path fill="currentColor" d="M6 2h12v6H6zm-2 8h16v12H4z" /></svg>
                  <div>
                    <p className="font-semibold">Personal item</p>
                    <p className="text-muted">1 piece per person · Each ≤ 40 × 30 × 10 cm · must fit under the seat.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-neon shrink-0"><path fill="currentColor" d="M9 2h6v3h3a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3z" /></svg>
                  <div>
                    <p className="font-semibold">Carry-on baggage</p>
                    <p className="text-muted">1 piece per person · ≤ 56 × 36 × 23 cm · total dimensions ≤ 115 cm.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-neon shrink-0"><path fill="currentColor" d="M8 2h8v4h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2z" /></svg>
                  <div>
                    <p className="font-semibold">Checked baggage</p>
                    <p className="text-muted">1 piece per person, 15 kg · ≤ 81 × 119 × 119 cm · purchasable from ₹2,291 (3 kg total).</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted mt-4">*Baggage dimensions include wheels and handles.</p>
            </div>
          )}
          {tab === 2 && (
            <div className="bagpane">
              <h3 className="font-display font-bold mb-2">Flight cancellation &amp; change policies</h3>
              <p className="text-muted text-sm mb-4">The ticket policy is subject to the rules below; these may differ from the airline's website.</p>
              <p className="font-semibold text-sm mb-2">Cancellation fee (per passenger)</p>
              <div className="rounded-xl border border-line overflow-hidden text-sm mb-4">
                <div className="grid grid-cols-2 bg-night/60 px-4 py-2 font-semibold"><span>Request time</span><span>Adult tickets</span></div>
                <div className="grid grid-cols-2 px-4 py-2 border-t border-line"><span className="text-neon">Current period · ≥2h before departure</span><span>₹5,540.00</span></div>
                <div className="grid grid-cols-2 px-4 py-2 border-t border-line"><span className="text-muted">After departure / within 2h</span><span className="text-muted">Non-refundable</span></div>
              </div>
              <p className="font-semibold text-sm mb-2">Change fee (within the same class)</p>
              <div className="rounded-xl border border-line overflow-hidden text-sm">
                <div className="grid grid-cols-2 bg-night/60 px-4 py-2 font-semibold"><span>Request time</span><span>Adult tickets</span></div>
                <div className="grid grid-cols-2 px-4 py-2 border-t border-line"><span className="text-neon">Current period · ≥2h before departure</span><span>₹5,540.00</span></div>
                <div className="grid grid-cols-2 px-4 py-2 border-t border-line"><span className="text-muted">After departure / within 2h</span><span className="text-muted">Non-changeable</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
